import { useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Combobox } from '../../components/ui/Combobox';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { ApiError } from '../../lib/api';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import { narrowMode } from '../../lib/portalAssets/sortSearchView';
import { operationalAccounts } from '../../lib/portalAssets/pageState';
import {
  chooseBrand,
  chooseClub,
  dateRangeIsBackwards,
  EMPTY_STAFF_ALL_FORM,
  hasAnyStaffAllFilter,
  staffAllEmptySentence,
  staffAllParams,
  type StaffAllForm,
} from '../../lib/portalAssets/staffAllView';
import { listAccounts, listUsers } from '../accounts';
import { AssetFilterBar, FilterField } from './AssetFilterBar';
import { AssetGrid } from './AssetGrid';
import { BulkActionBar } from './BulkActionBar';
import { StaffAssetDialog } from './StaffAssetDialog';
import { listAllAssets, listAssetCategories, staffBulkApi } from './staffApi';
import { useBulkSelection } from './useBulkSelection';
import { useImageErrorRefetch } from './useImageErrorRefetch';

// THE STAFF CROSS-ACCOUNT LIST (2026-10-09) — every live upload in the agency,
// filtered by brand / club / category / review status / media type / lifecycle /
// upload date. A tile opens the shared StaffAssetDialog (review, delete, categories)
// over this page, keyed by ?asset=<id>. There is no second copy of that dialog.
//
// ⚠️ STAFF ENDPOINTS ONLY (staffApi.ts). Every Kyck staff member sees every account
// here; there is no scope to resolve.
// ⚠️ A purged asset appears nowhere, which can leave a page SHORTER than usual;
// "Load more" still continues correctly (staffList.ts).

const ANY = '';

export function StaffAllAssetsSection() {
  // The open asset lives in the URL (?asset=<id>) so back closes it and the page is
  // linkable. The section stays mounted across it, so the filter form (local state)
  // and the loaded pages survive opening and closing an asset.
  const [urlParams, setUrlParams] = useSearchParams();
  const openId = urlParams.get('asset');
  const openAsset = (id: string) => {
    const next = new URLSearchParams(urlParams);
    next.set('asset', id);
    setUrlParams(next);
  };
  const closeAsset = () => {
    const next = new URLSearchParams(urlParams);
    next.delete('asset');
    setUrlParams(next, { replace: true });
  };
  // `?manager=<uid>` pre-filters to one account manager's clubs (the AM dashboard's "View all").
  const [form, setForm] = useState<StaffAllForm>(() => ({
    ...EMPTY_STAFF_ALL_FORM,
    accountManagerId: urlParams.get('manager') ?? '',
  }));
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });

  const brands = useMemo(
    () => [
      { value: ANY, label: 'All brands' },
      ...(accounts.data ?? [])
        .filter((a) => a.accountType === 'brand')
        .map((a) => ({ value: a.id, label: a.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [accounts.data],
  );
  const clubs = useMemo(
    () => [
      { value: ANY, label: 'All clubs' },
      ...operationalAccounts(accounts.data ?? []).map((a) => ({ value: a.id, label: a.name })),
    ],
    [accounts.data],
  );
  const groups = useMemo(
    () => [
      { value: ANY, label: 'All groups' },
      ...(accounts.data ?? [])
        .filter((a) => a.accountType === 'group')
        .map((a) => ({ value: a.id, label: a.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [accounts.data],
  );
  const users = useQuery({ queryKey: ['users', 'all'], queryFn: () => listUsers() });
  const managers = useMemo(
    () => [
      { value: ANY, label: 'All account managers' },
      ...(users.data ?? [])
        .map((u) => ({ value: u.uid, label: u.displayName || u.email }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [users.data],
  );
  const names = useMemo(() => new Map((accounts.data ?? []).map((a) => [a.id, a.name])), [accounts.data]);

  const categories = useQuery({
    queryKey: ['portal-assets-staff', 'categories', form.brandAccountId],
    queryFn: () => listAssetCategories(form.brandAccountId),
    enabled: form.brandAccountId !== '',
  });
  const categoryOptions = (categories.data?.categories ?? []).map((c) => ({ value: c.id, label: c.name }));

  // ⚠️ The box disables the other filters at once (raw), while the QUERY follows the debounced text so typing does not
  // fire a request per keystroke. The disabled values stay in the form: clearing the search restores the page as it was.
  const debouncedSearch = useDebouncedValue(form.search, 300);
  const backwards = narrowMode(form) === null && dateRangeIsBackwards(form);
  const listParams = staffAllParams({ ...form, search: debouncedSearch });
  const list = useInfiniteQuery({
    queryKey: ['portal-assets-staff', 'all', listParams],
    queryFn: ({ pageParam }) => listAllAssets({ ...listParams, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !backwards,
    retry: false,
  });
  const onImageError = useImageErrorRefetch(list.refetch);
  const tiles = list.data?.pages.flatMap((p) => p.tiles) ?? [];
  // STAFF BULK (2026-10-13). ⚠️ Category actions need ONE brand's categories: tiles carry no brand id (leak
  // guard), so they are offered only while the Brand filter is set. A selection can still span brands inside
  // that filter's clubs or groups — the server refuses per asset and the result names them.
  const qc = useQueryClient();
  const bulk = useBulkSelection(tiles);
  const bulkCategories = form.brandAccountId !== '' && categories.data ? categories.data.categories : null;
  const filtered = hasAnyStaffAllFilter(form);
  // ⚠️ "No clubs match" is a roster answer, not an empty asset list — the server says so
  // without querying (staffList.ts), and the page words it differently.
  const noMatchingClubs = list.data?.pages[0]?.noMatchingClubs === true;

  return (
    <section className="space-y-4">
      <p className="max-w-prose text-sm text-muted-foreground">
        Every photo and video clubs have uploaded, newest first. Filter by brand, group, account manager or club —
        they combine. Open one to review it, delete it or change its categories. Deleted assets are not shown
        here — they are under each club's “Recently deleted”.
      </p>
      <AssetFilterBar
        form={form}
        setForm={setForm}
        categories={categoryOptions}
        showCategory={form.brandAccountId !== ''}
        showKyckFilter
        filtered={filtered}
        onClear={() => setForm((f) => ({ ...EMPTY_STAFF_ALL_FORM, sort: f.sort }))}
        roster={
          <>
            <FilterField label="Brand">
              <Combobox
                value={form.brandAccountId}
                onValueChange={(v) => setForm((f) => chooseBrand(f, v))}
                options={brands}
                placeholder="All brands"
                searchPlaceholder="Search brands…"
                aria-label="Brand"
              />
            </FilterField>
            <FilterField label="Group">
              <Combobox
                value={form.groupAccountId}
                onValueChange={(v) => setForm((f) => ({ ...f, groupAccountId: v }))}
                options={groups}
                placeholder="All groups"
                searchPlaceholder="Search groups…"
                aria-label="Group"
              />
            </FilterField>
            <FilterField label="Account manager">
              <Combobox
                value={form.accountManagerId}
                onValueChange={(v) => setForm((f) => ({ ...f, accountManagerId: v }))}
                options={managers}
                placeholder="All account managers"
                searchPlaceholder="Search account managers…"
                aria-label="Account manager"
              />
            </FilterField>
            <FilterField label="Club">
              <Combobox
                value={form.accountId}
                onValueChange={(v) => setForm((f) => chooseClub(f, v))}
                options={clubs}
                placeholder="All clubs"
                searchPlaceholder="Search clubs…"
                aria-label="Club"
              />
            </FilterField>
          </>
        }
      />

      {backwards ? (
        <p className="text-sm text-destructive" role="alert">
          The start date is after the end date.
        </p>
      ) : list.isPending ? (
        <CenteredSpinner />
      ) : list.isError ? (
        <p className="text-sm text-muted-foreground">
          {list.error instanceof ApiError && list.error.status === 400 ? list.error.message : "Couldn't load assets."}
        </p>
      ) : tiles.length === 0 && !list.hasNextPage ? (
        <p className="text-sm text-muted-foreground">{staffAllEmptySentence(filtered, noMatchingClubs)}</p>
      ) : (
        <>
          <BulkActionBar
            tiles={bulk.selectedTiles}
            categories={bulkCategories}
            categoryNote="Set the Brand filter to choose a category — categories belong to one brand."
            onClear={bulk.clear}
            onKeepOnly={bulk.keepOnly}
            onDone={() => void qc.invalidateQueries({ queryKey: ['portal-assets-staff'] })}
            selectNote={bulk.note}
            onSelectAll={bulk.selectAllVisible}
            api={staffBulkApi}
            audience="staff"
          />
          <AssetGrid
            fill
            tiles={tiles}
            onOpen={(t) => openAsset(t.id)}
            onImageError={onImageError}
            accountName={(id) => names.get(id) ?? null}
            selection={{ isSelected: bulk.isSelected, onToggle: bulk.toggle }}
          />
        </>
      )}
      {list.hasNextPage && (
        <Button size="sm" variant="outline" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
          {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
        </Button>
      )}
      <StaffAssetDialog assetId={openId} onClose={closeAsset} />
    </section>
  );
}
